"use client";

import React, { useMemo } from "react";
import { getColorFromDivergingScale, getColorFromSequentialScale } from "@/utils/colors";
import type { CellComponentProps } from "../DataTable.types";

export interface HorizontalBarCellProps extends CellComponentProps {
  value: number;
  min: number;
  max: number;
  color?: string | "auto";
  backgroundColor?: string | "none";
  colorScale?: {
    type: "sequential" | "diverging";
    scheme?: string;
    center?: number;
  };
  showLabel?: boolean;
  formatLabel?: (value: number) => string;
  height?: number;
  minWidth?: number;
  segments?: number;
  segmentGap?: number;
  barWidth?: number | string;
}

/**
 * HorizontalBarCell
 *
 * Horizontal comparison bar with automatic color scaling.
 * Perfect for showing relative values with color-coded performance indicators.
 * Supports segmented "power meter" style with optional gradient coloring.
 */
export const HorizontalBarCell = React.memo<HorizontalBarCellProps>(
  ({
    value,
    min,
    max,
    color = "var(--color-viz-default)",
    backgroundColor = "var(--color-background-muted)",
    colorScale,
    showLabel = true,
    formatLabel,
    height = 20,
    minWidth = 4,
    segments,
    segmentGap = 2,
    barWidth,
    context,
  }) => {
    // Calculate percentage width
    const percentage = useMemo(() => {
      if (max === min) return 100;
      return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
    }, [value, min, max]);

    // Get the effective color
    const effectiveColor = useMemo(() => {
      if (color === "auto" && colorScale) {
        if (colorScale.type === "diverging" && colorScale.center !== undefined) {
          return getColorFromDivergingScale(value, min, max, colorScale.center, colorScale.scheme);
        }
        return getColorFromSequentialScale(value, min, max, colorScale.scheme);
      }
      return color;
    }, [color, colorScale, value, min, max]);

    // Loading state
    if (context.isLoading) {
      return (
        <div className="flex items-center gap-2 w-full" style={{ height }}>
          <div className="h-full flex-1 animate-pulse bg-border-default rounded" />
        </div>
      );
    }

    // Segmented rendering
    if (segments && segments > 0) {
      const totalGapWidth = segmentGap * (segments - 1);
      const segmentWidth = `calc((100% - ${totalGapWidth}px) / ${segments})`;
      const filledSegments = Math.ceil((percentage / 100) * segments);

      const barContainerStyle: React.CSSProperties = {
        height,
        ...(barWidth && {
          width: typeof barWidth === "number" ? `${barWidth}px` : barWidth,
          flexShrink: 0,
        }),
      };

      return (
        <div className="flex items-center gap-2 w-full">
          <div className={`flex gap-0.5 ${barWidth ? "" : "flex-1"}`} style={barContainerStyle}>
            {Array.from({ length: segments }).map((_, i) => {
              const isFilled = i < filledSegments;

              // All filled segments use the same color based on the actual value
              // This makes it easy to scan and compare values across rows
              return (
                <div
                  key={i}
                  className="rounded-sm"
                  style={{
                    width: segmentWidth,
                    backgroundColor: isFilled
                      ? effectiveColor
                      : backgroundColor === "none"
                        ? "transparent"
                        : backgroundColor,
                    marginRight: i < segments - 1 ? `${segmentGap}px` : 0,
                  }}
                />
              );
            })}
          </div>
          {showLabel && (
            <span className="text-sm text-text-body whitespace-nowrap">
              {formatLabel ? formatLabel(value) : value.toLocaleString()}
            </span>
          )}
        </div>
      );
    }

    // Standard bar rendering
    const barContainerStyle: React.CSSProperties = {
      height,
      backgroundColor: backgroundColor === "none" ? "transparent" : backgroundColor,
      ...(barWidth && {
        width: typeof barWidth === "number" ? `${barWidth}px` : barWidth,
        flexShrink: 0,
      }),
    };

    return (
      <div className="flex items-center gap-2 w-full">
        <div className={`relative rounded-sm ${barWidth ? "" : "flex-1"}`} style={barContainerStyle}>
          <div
            className="absolute left-0 top-0 h-full rounded-sm"
            style={{
              width: `${percentage}%`,
              minWidth: percentage > 0 ? minWidth : 0,
              backgroundColor: effectiveColor,
            }}
          />
        </div>
        {showLabel && (
          <span className="text-sm text-text-body whitespace-nowrap">
            {formatLabel ? formatLabel(value) : value.toLocaleString()}
          </span>
        )}
      </div>
    );
  }
);

HorizontalBarCell.displayName = "HorizontalBarCell";
